/**
 * PracticeSubPageTemplate — long-form practice sub-page (family law cluster).
 * Article layout with hero, body sections, optional confidential-support note,
 * FAQ, booking CTA, and Article + BreadcrumbList + FAQPage JSON-LD.
 */
import { useEffect } from 'react';
import { Phone, Calendar, ShieldCheck, ChevronRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import CalendarEmbed from '@/components/CalendarEmbed';
import type { PracticeSubPage } from '@/data/practiceSubPages';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';

export default function PracticeSubPageTemplate({ page }: { page: PracticeSubPage }) {
  const canonical = `${SITE}${page.url}`;
  const dates = pageDates(page.url);
  const metaTitle = `${page.title} | Morgan Legal Group`;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.title,
      description: page.metaDescription,
      mainEntityOfPage: canonical,
      datePublished: dates.published,
      dateModified: dates.modified,
      author: { '@type': 'LegalService', name: 'Morgan Legal Group, P.C.', url: SITE },
      publisher: {
        '@type': 'Organization',
        name: 'Morgan Legal Group, P.C.',
        logo: { '@type': 'ImageObject', url: `${SITE}/brand/morgan-logo.png` },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: page.parentLabel, item: `${SITE}${page.parentHref}` },
        { '@type': 'ListItem', position: 3, name: page.title, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: metaTitle, description: page.metaDescription, url: canonical });
    }
  }, [metaTitle, page.metaDescription, canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-navy pt-28 lg:pt-32 pb-14 lg:pb-16 overflow-hidden">
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide flex items-center flex-wrap gap-1">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <ChevronRight className="w-3 h-3 text-gold/60" />
              <a href={page.parentHref} className="hover:text-gold transition-colors">{page.parentLabel}</a>
              <ChevronRight className="w-3 h-3 text-gold/60" />
              <span className="text-white/80">{page.title}</span>
            </nav>
            <div className="section-label mb-5 text-gold">
              <span className="inline-block w-10 h-0.5 bg-gold align-middle mr-2" />
              {page.parentLabel} · New York
            </div>
            <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] mb-6">{page.title}</h1>
            <p className="font-body text-white/80 text-base lg:text-lg leading-relaxed mb-8">{page.heroIntro}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#book" className="btn-gold">
                <Calendar className="w-4 h-4" />
                {page.supportNote ? 'Free Confidential Consultation' : 'Schedule Your Free Consultation'}
              </a>
              <a href="tel:8885291315" className="btn-ghost-gold">
                <Phone className="w-4 h-4" />
                888-529-1315
              </a>
            </div>
          </div>
        </section>

        {/* Confidential support note (sensitive pages) */}
        {page.supportNote && (
          <section className="bg-gold/10 border-y border-gold/30">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <p className="font-body text-navy/80 text-sm leading-relaxed">
                Your conversation with us is <strong>free and completely confidential</strong>, with no pressure and no
                obligation. If you need immediate emotional support, the RAINN national hotline is available 24/7 at{' '}
                <a href="tel:8006564673" className="text-navy font-semibold hover:text-gold">800-656-HOPE (4673)</a>.
              </p>
            </div>
          </section>
        )}

        {/* Body */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            {page.sections.map((sec, i) => (
              <article key={i}>
                <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold mb-4">{sec.heading}</h2>
                <div className="space-y-3">
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed">{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="section-label mb-3">Frequently Asked Questions</div>
            <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold mb-8">{page.title} — FAQ</h2>
            <div className="space-y-4">
              {page.faqs.map((f) => (
                <details key={f.q} className="group bg-white border border-navy/10 p-5">
                  <summary className="flex items-start justify-between gap-3 cursor-pointer list-none font-display text-navy font-semibold">
                    {f.q}
                    <span className="text-gold text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="font-body text-navy/75 text-sm leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Booking */}
        <section id="book" className="bg-white py-16 lg:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="section-label mb-3 justify-center text-gold">{page.supportNote ? 'Free & Confidential' : 'Free Consultation'}</div>
            <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold mb-3">Talk to a New York Attorney</h2>
            <p className="font-body text-navy/70 text-base mb-8">
              Choose a time that works for you, or call <a href="tel:8885291315" className="text-gold hover:underline">888-529-1315</a>.
            </p>
            <div className="bg-white border border-navy/10 shadow-xl overflow-hidden text-left">
              <CalendarEmbed height={640} title={`Morgan Legal Group — ${page.title}`} />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
